/**
 * InboundSetupGuide — shows the tenant's receive address plus a ready-to-copy
 * supplier text block ("forward invoices as an attachment to rechnung@…"),
 * WL-A6. Owner context only (the address is the caller's own mailbox).
 *
 * @module InboundSetupGuide
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconMailOpen, IconCopy, IconCheck } from '../../icons';

interface InboundSetupGuideProps {
  /** The tenant's receive address, e.g. `mustermann@rechnung.faebill.com`. */
  address: string;
}

const InboundSetupGuide: React.FC<InboundSetupGuideProps> = ({ address }) => {
  const { t } = useTranslation('posteingang');
  const [copied, setCopied] = useState(false);

  const supplierTemplate = t(
    'setup.template',
    'Bitte senden Sie Ihre Rechnungen künftig als PDF- oder XML-Anhang an: {{address}}',
    { address },
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(supplierTemplate);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions/insecure context) — non-critical.
      setCopied(false);
    }
  }, [supplierTemplate]);

  return (
    <div className="bg-surface-raised border border-hairline rounded-xl p-4 md:p-5">
      <div className="flex items-center gap-2 mb-3">
        <IconMailOpen className="text-accent-ink" />
        <h2 className="text-primary font-semibold">
          {t('setup.title', 'So erhalten Sie Rechnungen')}
        </h2>
      </div>

      <p className="text-sm text-muted mb-2">
        {t('setup.addressLabel', 'Ihre Empfangsadresse:')}
      </p>
      <div className="bg-surface border border-hairline rounded-lg px-3 py-2 mb-3 font-mono text-sm text-accent-ink break-all">
        {address}
      </div>

      <p className="text-sm text-muted mb-2">
        {t(
          'setup.hint',
          'Geben Sie diese Adresse an Ihre Lieferanten weiter — z. B. mit diesem Textbaustein:',
        )}
      </p>
      <div className="bg-surface border border-hairline rounded-lg p-3 text-sm text-secondary">
        {supplierTemplate}
      </div>

      <button
        onClick={handleCopy}
        className="mt-3 inline-flex items-center gap-2 text-sm bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg font-medium transition-colors"
      >
        {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
        {copied ? t('setup.copied', 'Kopiert') : t('setup.copy', 'Textbaustein kopieren')}
      </button>
    </div>
  );
};

export default InboundSetupGuide;
